import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import styled from "@emotion/styled";
import { spacing } from "@mui/system";
import {
  Box,
  Breadcrumbs as MuiBreadcrumbs,
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

import axios from "../../../utils/axios";
import { useDialog } from "../../../contexts/DialogContext";
import { showErrorSnackbar } from "../../../redux/slices/snackbar";
import { getTimeStamp } from "../../../utils/common";
import useWindowDimensions from "../../../utils/windowDimensions";
import DeleteBackupViewer, { formatDate, parseJson } from "../../../components/DeleteBackupViewer";

const Breadcrumbs = styled(MuiBreadcrumbs)(spacing);

function UserDeleteHistoryDetail({ historyId }) {
  const dispatch = useDispatch();
  const [history, setHistory] = useState(null);
  const historyUrl = "/radius/user-delete/history/" + historyId;

  useEffect(() => {
    axios
      .get(historyUrl)
      .then((response) => setHistory(response.data))
      .catch((err) => dispatch(showErrorSnackbar(err.message || "Failed to load history")));
  }, [historyId]);

  if (!history) {
    return (
      <Box display="flex" justifyContent="center" p={6}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <DeleteBackupViewer
      historyUrl={historyUrl}
      info={[
        ["Users", `${history.userNames} (${history.userCount})`],
        ["Franchise", history.tenantNames],
        ["Reason", history.remarks || "-"],
        ["Deleted By", history.deletedBy],
        ["Deleted At", formatDate(history.deletedAt)],
        ["IP Address", history.ipAddress],
      ]}
      summary={parseJson(history.summary, [])}
      totalRows={history.totalRows}
      fileName={`user-delete-backup-${historyId}.json`}
    />
  );
}

function UserDeleteHistory() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { openDialog } = useDialog();
  const { height } = useWindowDimensions();

  const [rows, setRows] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });

  const fetchData = async () => {
    try {
      setIsLoading(true);
      const response = await axios.get("/radius/user-delete/history", {
        params: { search, page: paginationModel.page, size: paginationModel.pageSize },
      });
      setRows(response.data.content);
      setRowCount(response.data.totalElements);
    } catch (err) {
      dispatch(showErrorSnackbar(err.message || "Failed to load delete history"));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(fetchData, 300);
    return () => clearTimeout(timer);
  }, [paginationModel, search]);

  const openDetail = (historyId) =>
    openDialog(
      "user_delete_history",
      "Deleted Users Details",
      <UserDeleteHistoryDetail key={getTimeStamp()} historyId={historyId} />,
      "md",
    );

  const columns = [
    {
      field: "userNames",
      headerName: "Users",
      width: 280,
      renderCell: (params) => (
        <Button
          size="small"
          sx={{ justifyContent: "flex-start", textTransform: "none" }}
          onClick={() => openDetail(params.row.userDeleteHistoryId)}
        >
          {params.value}
        </Button>
      ),
    },
    { field: "userCount", headerName: "Count", width: 80 },
    { field: "tenantNames", headerName: "Franchise", width: 200 },
    { field: "remarks", headerName: "Reason", width: 200 },
    { field: "deletedBy", headerName: "Deleted By", width: 150 },
    {
      field: "deletedAt",
      headerName: "Deleted At",
      width: 190,
      renderCell: (params) => formatDate(params.value),
    },
    { field: "totalRows", headerName: "Records Deleted", width: 140 },
    { field: "ipAddress", headerName: "IP Address", width: 140 },
  ];

  return (
    <Grid container spacing={6}>
      <Grid item xs={12}>
        <Card>
          <CardHeader
            title={<Typography variant="h6">Deleted Users History</Typography>}
            subheader={
              <Breadcrumbs aria-label="Breadcrumb" mt={2}>
                <Typography>Users</Typography>
                <Typography>Suspended Users</Typography>
                <Typography>Delete History</Typography>
              </Breadcrumbs>
            }
            action={
              <Box display="flex" gap={2} alignItems="center">
                <TextField
                  size="small"
                  placeholder="Search user / franchise / deleted by"
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPaginationModel((prev) => ({ ...prev, page: 0 }));
                  }}
                />
                <Button
                  variant="outlined"
                  startIcon={<ArrowBackIcon />}
                  onClick={() => navigate("/user/suspended")}
                >
                  Back
                </Button>
              </Box>
            }
          />
          <CardContent sx={{ height: height - 180 }}>
            <DataGrid
              rows={rows}
              columns={columns}
              getRowId={(row) => row.userDeleteHistoryId}
              rowCount={rowCount}
              loading={isLoading}
              paginationMode="server"
              paginationModel={paginationModel}
              onPaginationModelChange={setPaginationModel}
              pageSizeOptions={[25, 50, 100]}
              disableColumnFilter
              sortingMode="server"
            />
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
}

export default UserDeleteHistory;
